import { ILogger } from 'library/ts/utilities/logger/interfaces/ILogger';
import { LogService } from 'library/ts/utilities/logger/LogService';
import { Utilities } from 'library/ts/utilities/Utilities';
import { IAdUi } from 'tag/ts/tag/interfaces/ads/IAdUi';
import { ICustomAdUiElements } from 'tag/ts/tag/interfaces/tracking/ICustomAdUiElements';

/**
 * @copyright © Paramount Streaming
 * @author  Daniel Silva
 * @description Notify Google SDK of custom ad ui elements Smart Tag sets for tracking - singleton
 */
export class CustomAdUiElements implements ICustomAdUiElements {

	private static _instance: CustomAdUiElements = new CustomAdUiElements();
	private _uiElements: Map<string, HTMLElement> = new Map();
	private logger: ILogger;

	constructor() {
		if ( CustomAdUiElements._instance ) {
			throw new Error( this.name + ' is a Singleton and can only be accessed through ' + this.name + '.getInstance()' );
		}
		CustomAdUiElements._instance = this;
		this.logger = LogService.create( { prefixes: [ 'CustomAdUiElements:' ] } );
	}

	get name(): string {
		return 'CustomAdUiElements';
	}

	get utils(): Utilities {
		return window?.SmartTag?.utilities;
	}

	/**
	 * @description Returns the singleton instance
	 * @returns {CustomAdUiElements}
	 */
	public static getInstance(): CustomAdUiElements {
		return CustomAdUiElements._instance;
	}

	/**
	 * @description Registers an element by key
	 * @param {string} key
	 * @param {HTMLElement} element
	 */
	setElement( key: string, element: HTMLElement ): void {
		if ( !this.utils?.Type?.isElement( element ) ) {
			this.logger?.warn( 'setElement: Invalid element provided for key:', key );
			return;
		}
		this._uiElements.set( key, element );
	}

	/**
	 * @description Returns a shallow copy of registered UI elements map
	 * @returns {Map<string, HTMLElement>}
	 */
	getElements(): Map<string, HTMLElement> {
		return new Map( this._uiElements );
	}

	/**
	 * @description Informs the ad SDK of the registered elements
	 * @param {IAdUi} adUi
	 */
	notifySDK( adUi: IAdUi ): void {
		if ( !this.utils?.Type?.isFunction( adUi?.setVisibleElements ) ) {
			this.logger?.warn( 'notifySDK: setVisibleElements not available on adUi object' );
			return;
		}
		adUi.setVisibleElements( this.getElements() );
		this.logger?.log( 'notifying SDK of custom ad ui elements:\n', this.getElements() );
	}

	/**
	 * @description Clears all registered UI elements
	 */
	clear(): void {
		this._uiElements.clear();
	}
}